import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KeycloakService } from 'keycloak-angular';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Refreshes the Keycloak access token before each backend API call,
 * and on refresh failure terminates the user's SAML session via SLO.
 *
 * Behaviour per request:
 *   - Skip non-admin-API URLs and unauthenticated state (let the
 *     other interceptors / KeycloakAuthGuard handle those).
 *   - Otherwise call `keycloak.updateToken(60)` — a no-op if the
 *     token has ≥ 60 s left, an HTTP refresh against KC otherwise.
 *   - If refresh fails (KC's SSO session has expired in line with
 *     the JWT, per `ssoSessionMaxLifespan = accessTokenLifespan` on
 *     the realm), show the OAM-prescribed "session expired" message
 *     for 5 seconds, then call `keycloak.logout()`. KC sends a SAML
 *     `LogoutRequest` (NameID + SessionIndex) to OAM's SLO endpoint
 *     so OAM clears its session too — guaranteeing a real OAM
 *     re-auth on the next login attempt.
 */
@Injectable()
export class KeycloakRefreshInterceptor implements HttpInterceptor {
  private logoutScheduled = false;

  constructor(
    private readonly keycloak: KeycloakService,
    private readonly snackBar: MatSnackBar
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.shouldHandle(req)) {
      return next.handle(req);
    }

    // Order matters: `catchError` BEFORE `switchMap` so it only sees
    // failures from `updateToken` (i.e. the SSO session is dead),
    // not downstream HTTP errors from `next.handle(req)` (4xx/5xx from
    // the backend are normal API errors and must not trigger SLO).
    return from(this.keycloak.updateToken(60)).pipe(
      catchError((err: unknown) => {
        this.scheduleSamlSlo();
        return throwError(() => err);
      }),
      switchMap(() => next.handle(req))
    );
  }

  private shouldHandle(req: HttpRequest<unknown>): boolean {
    if (!req.url.startsWith(environment.apiHost)) {
      return false;
    }
    try {
      return this.keycloak.isLoggedIn();
    } catch {
      return false;
    }
  }

  /**
   * Show the user-visible message for ~5 s, then log out via Keycloak.
   * `keycloak.logout()` on a SAML-broker user automatically POSTs a
   * SAML LogoutRequest to OAM (via the IdP's singleLogoutServiceUrl)
   * so OAM clears its session in lock-step.
   *
   * Guarded against multiple parallel refresh failures firing the
   * sequence repeatedly within a single 5-second window.
   */
  private scheduleSamlSlo(): void {
    if (this.logoutScheduled) {
      return;
    }
    this.logoutScheduled = true;

    this.snackBar.open(
      'Your session has expired. Signing you out securely…',
      'OK',
      { duration: 5000 }
    );

    setTimeout(() => {
      // When the SAML login UI is off, sending the user to /login would
      // briefly render the (disabled) SSO-first page before the auth
      // guard bounced them on. Send them to `/` instead — the guard
      // catches it and calls keycloak.login() so they land directly on
      // KC's hosted login. New UI is the default; only an explicit
      // `enableSamlLoginUi: false` engages the legacy path.
      const postLogoutPath =
        environment.enableSamlLoginUi !== false ? '/login' : '/';
      void this.keycloak.logout(window.location.origin + postLogoutPath);
    }, 5000);
  }
}
